import React, { useState } from 'react';
import { Plus, Search } from 'lucide-react';
import AssetLevelRules from './AssetLevelRules';
import ColumnLevelRules from './ColumnLevelRules';
// import DimensionWeightage from './DimensionWeightage';
import DimensionWeightage from '../../../pages/dataAssets/dimensionWeightage';
import AddAssetRuleModal from './AddAssetRuleModal';
import AddColumnRuleModal from './AddColumnRuleModal';
import { CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';

function DataQualityMain({setOpenModal, assetId, assetName, productId}) {
  const [activeTab, setActiveTab] = useState('asset');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // New state for API data
  const [allRulesData, setAllRulesData] = useState(null); // /rule/getAllRules
  const [assetRules, setAssetRules] = useState([]); // /rule/assetRules/{assetId}
  const [assetInfo, setAssetInfo] = useState(null); // /rule/assetInfo/{assetId}
  const [columnRules, setColumnRules] = useState([ ]);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [dataFrame, setDataFrame] = useState("")
  // Edit modal state for asset rules
  const [editRuleData, setEditRuleData] = useState(null);
  const [isAssetModalOpen, setIsAssetModalOpen] = useState(false);
  // Edit modal state for column rules
  const [editColumnRuleData, setEditColumnRuleData] = useState(null);
  const [isColumnModalOpen, setIsColumnModalOpen] = useState(false);

  const [hierarchicalData, setHierarchicalData] = useState([]);

  const tabs = [
    { id: 'asset', label: 'Asset Level Rules', active: activeTab === 'asset' },
    { id: 'column', label: 'Column Level Rules', active: activeTab === 'column' },
    { id: 'dimension', label: 'Dimension Weightage', active: activeTab === 'dimension' }
  ];

  const handleAddRule = (newRule) => {
    const rule = {
      id: Date.now().toString(),
      dimension: newRule.dimension || '',
      ruleName: newRule.ruleName || '',
      description: newRule.description || '',
      dqRuleCheck: newRule.dqRuleCheck || '',
      ...newRule
    };
    if (activeTab === 'asset') {
      setAssetRules([...assetRules, rule]);
    } else if (activeTab === 'column') {
      setColumnRules([...columnRules, rule]);
    }
    setIsModalOpen(false);
  };

  // Handler for editing asset rules
  const handleEditAssetRule = (ruleDetails) => {
    setEditRuleData(ruleDetails);
    setIsAssetModalOpen(true);
  };

  const handleCloseAssetModal = () => {
    setIsAssetModalOpen(false);
    setEditRuleData(null);
  };

  // Handler for editing column rules
  const handleEditColumnRule = (ruleDetails) => {
    setEditColumnRuleData(ruleDetails);
    setIsColumnModalOpen(true);
  };

  const handleCloseColumnModal = () => {
    setIsColumnModalOpen(false);
    setEditColumnRuleData(null);
  };

  const fetchAllData = React.useCallback(() => {
    if (assetId) {
      const BASE_URL = process.env.REACT_APP_API_URL;
      setLoading(true);
      setApiError(null);
  
      // Fetch all rules/dimensions
      const allRulesPromise = axios.get(`${BASE_URL}/rule/getAllRules`)
        .then(res => setAllRulesData(res.data))
        .catch(err => { setApiError('Error fetching all rules'); });

      const prodHierarchyDataPromise = axios.get(`${BASE_URL}/hierarchy/${productId}`)
        .then(res => {
          const result = [];
          for (const key in res.data) {
          if (res.data[key] !== 'N/A' && res.data[key] !== null) {
            result.push(res.data[key]);
          }}
          setHierarchicalData(result);
        })
        .catch(err => { setApiError('Error fetching product hierarchy data'); });
  
      // Fetch asset rules
      const assetRulesPromise = axios.get(`${BASE_URL}/rule/assetRules/${assetId}`)
        .then(res => {
          // Map API keys to UI keys
          const mapped = (res.data || []).map(rule => ({
            id: rule.rule_id,
            ruleName: rule.rule_name,
            description: rule.rule_description,
            keywords: rule.keywords,
            dimension: rule.dimension_name,
            dqRuleCheck: rule.check_name,
            checkType: rule.check_type,
            columnName: rule.attributes, // for column level
            isActive: rule.is_active,
          }));
          setAssetRules(mapped.filter(rule => rule.checkType === 'Asset Level Check'));
          setColumnRules(mapped.filter(rule => rule.checkType === 'Column Level Check'));
        })
        .catch(err => { setApiError('Error fetching asset rules'); });
  
      // Fetch asset info (columns)
      const assetInfoPromise = axios.get(`${BASE_URL}/rule/assetInfo/${assetId}`)
        .then(res => {
          setAssetInfo(res.data);
          // Handle new API structure that might not have dataframe_id
          if (res.data && res.data.dataframe_id) {
            setDataFrame(res.data.dataframe_id);
          } else {
            // If no dataframe_id in new structure, set a default or handle accordingly
            setDataFrame("");
          }
        })
        .catch(err => { setApiError('Error fetching asset info'); });
  
      Promise.all([allRulesPromise, prodHierarchyDataPromise, assetRulesPromise, assetInfoPromise])
        .finally(() => setLoading(false));
    }
  }, [assetId, productId]);

  // Fetch all rules/dimensions and asset rules when popup opens
  React.useEffect(() => {
  fetchAllData()
  }, [fetchAllData]);

  return (
    <div className="fixed mx-auto my-auto inset-0  bg-black bg-opacity-50 flex items-center justify-center  p-4" style = {{zIndex: "9999"}} >
      {/* Loading Spinner Overlay */}
      {loading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(255,255,255,0.7)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <CircularProgress size={60} thickness={5} color="primary" />
        </div>
      )}
      <div className="w-[1300px] max-w-[1350px] min-w-[1300px] max-h-[680px] min-h-[680px] h-[680px] bg-white  rounded-lg  overflow-y-auto ">
        <div className="mb-4 px-4  py-4 border flex justify-between ">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">DQ Rule Tagging</h1>
            <p className="">Configure and manage data quality rules</p>
          </div>
          <button onClick={() => {setOpenModal(false)}} ><CloseIcon /></button>
        </div>

        <div className="mx-auto px-12 pb-8  ">
          {/* Header */}
          {/* Tabs */}
          <div className="flex space-x-1 gap-2 mb-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                  tab.active
                    ? 'bg-[#bb1ccc] text-white shadow-lg'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Controls */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center space-x-4">
              {activeTab !== 'dimension' && (
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="flex items-center space-x-2 bg-[#319481] text-white px-4 py-2 rounded-lg hover:bg-[#319481] transition-colors duration-200 shadow-md bg-[#0000a0]"
                >
                  <Plus size={20} />
                  <span>Add New Rule</span>
                </button>
              )}
              {activeTab !== 'dimension' && (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder="Search columns..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0000a0] focus:border-transparent w-64"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {activeTab === 'asset' && (
              <AssetLevelRules
                rules={assetRules}
                searchTerm={searchTerm}
                fetchAllData={fetchAllData}
                onEdit={handleEditAssetRule}
              />
            )}
            
            {activeTab === 'column' && (
              <ColumnLevelRules
                rules={columnRules}
                searchTerm={searchTerm}
                fetchAllData={fetchAllData}
                onEdit={handleEditColumnRule}
              />
            )}
            {activeTab === 'dimension' && (
              <DimensionWeightage 
                assetId={assetId}
              />
            )}
          </div>
        </div>

        {/* Add Rule Modal */}
        {(isModalOpen && activeTab === 'asset') || isAssetModalOpen ? (
          <AddAssetRuleModal
            isOpen={isModalOpen || isAssetModalOpen}
            onClose={() => { setIsModalOpen(false); handleCloseAssetModal(); }}
            onAdd={handleAddRule}
            allRulesData={allRulesData}
            assetId={assetId}
            assetName={assetName}
            assetInfo={assetInfo}
            dataFrame={dataFrame}
            fetchAllData={fetchAllData}
            editRuleData={editRuleData}
            initialHierarchicalData={hierarchicalData}
          />
        ) : null}
        {(isModalOpen && activeTab === 'column') || isColumnModalOpen ? (
          <AddColumnRuleModal
            isOpen={isModalOpen || isColumnModalOpen}
            onClose={() => { setIsModalOpen(false); handleCloseColumnModal(); }}
            onAdd={handleAddRule}
            allRulesData={allRulesData}
            assetId={assetId}
            assetName={assetName}
            assetInfo={assetInfo}
            dataFrame={dataFrame}
            fetchAllData={fetchAllData}
            editRuleData={editColumnRuleData}
            initialHierarchicalData={hierarchicalData}
          />
        ) : null}
      </div>
    </div>
  );
}

export default DataQualityMain;